/* eslint-disable camelcase */
import { getApiConfiguration, toSnackCase } from '@/helpers/api';
import { RouteNames } from '@/router/types';
import { CreateForm, CreateFormFromJSON, FormsApi } from '@/services/generated/client';
import store from '@/store/index';
import { Route } from 'vue-router';
import {
  Action, config, Module, Mutation, MutationAction, VuexModule,
} from 'vuex-module-decorators';
import {
  RequisitionAccounts,
  Address,
  ConfigInterface,
  CustomerLoanData,
  CustomerPersonData,
  FormState,
  FormType,
  FormUTMParams,
  InvestmentLoanData,
  InvestmentPersonData, IrregularOffer, Offer, RawPersonInfo, RetrieveFormFixed, SetPersonField,
} from './types';

config.rawError = true;

// DEFAULT VALUES
const DEFAULT_CUSTORMER_LOAN_DATA: CustomerLoanData = {
  value: '50000',
  installmentValue: '1000',
};

const DEFAULT_INVESTMENT_LOAN_DATA: InvestmentLoanData = {
  value: '50000',
  installmentValue: '1000',
  purpose: '',
};

const DEFAULT_CUSTORMER_PERSON_DATA: CustomerPersonData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  birthNumber: '',
  permanentAddress: null,
  identityCard: '',
  realtyAddress: null,
  haveRealty: false,
};

const DEFAULT_INVESTMENT_PERSON_DATA: InvestmentPersonData = {
  ...DEFAULT_CUSTORMER_PERSON_DATA,
  selfEmployedCompany: '',
};

// API
let formsApi: FormsApi | null = null;
const getFormsApi = () => {
  if (!formsApi) formsApi = new FormsApi(getApiConfiguration());

  return formsApi;
};

@Module({
  name: 'form',
  namespaced: true,
  dynamic: true,
  store,
})
export default class FormStore extends VuexModule {
  // STATES
  initialLoadingCounter = 0;

  loaded = false;

  loadingCounter = 0;

  loan: CustomerLoanData | InvestmentLoanData | null = null;

  person: CustomerPersonData | InvestmentPersonData | null = null;

  debtors: (CustomerPersonData | InvestmentPersonData)[] = [];

  formConsent = false;

  config: ConfigInterface | null = null;

  formState: FormState | null = null;

  token = '';

  type: FormType | null = null;

  utmParams: FormUTMParams = {};

  isOffer = false;

  offerDraft: Offer | null = null;

  irregularOfferDraft: IrregularOffer | null = null;

  requisitionAccounts: RequisitionAccounts = {};

  // GETTERS
  get initialLoading() {
    return Boolean(this.initialLoadingCounter);
  }

  get loading() {
    return Boolean(this.loadingCounter);
  }

  get query() {
    return {
      ...this.type && { type: this.type },
      ...this.token && { token: this.token },
      ...this.utmParams?.utmSource && { utm_source: this.utmParams?.utmSource },
      ...this.utmParams?.utmCampaign && { utm_campaign: this.utmParams?.utmCampaign },
      ...this.utmParams?.utmMedium && { utm_medium: this.utmParams?.utmMedium },
    };
  }

  get noStateRoute(): Partial<Route> {
    return {
      name: RouteNames.Crossroad,
      query: this.query,
    };
  }

  get basicDealInfoRoute(): Partial<Route> {
    return {
      name: RouteNames.PersonData,
      query: this.query,
    };
  }

  get bankingInitRoute(): Partial<Route> {
    return {
      name: RouteNames.Banking,
      query: this.query,
    };
  }

  get bankingInfoRoute(): Partial<Route> {
    return {
      name: RouteNames.Cover,
      query: this.query,
    };
  }

  get collateralInfoRoute(): Partial<Route> {
    return {
      name: RouteNames.Documents,
      query: this.query,
    };
  }

  get personInfoData(): CreateForm {
    const { phoneE164, ...person } = this.person;

    return CreateFormFromJSON({
      data: toSnackCase({
        ...this.loan,
        ...person,
        phone: phoneE164 || person.phone,
        formConsent: this.formConsent,
        dealType: this.type,
      }),
      type: 'UNI_LOAN_LEAD_FORM',
    });
  }

  get bankingInfoData(): CreateForm {
    return CreateFormFromJSON({
      data: {},
      type: 'UNI_LOAN_LEAD_FORM',
    });
  }

  get collateralInfoData(): CreateForm {
    // TODO: realty data should be stored in collateralInfoData
    const { have_realty, realty_address } = this.personInfoData.data;

    return CreateFormFromJSON({
      data: toSnackCase({
        haveRealty: have_realty,
        realtyAddress: realty_address,
      }),
      type: 'UNI_LOAN_LEAD_FORM',
    });
  }

  // MUTATIONS
  @Mutation
  startInitialLoad() {
    this.initialLoadingCounter += 1;
  }

  @Mutation
  stopInitialLoad() {
    this.initialLoadingCounter -= 1;
  }

  @Mutation
  finishInitialLoading() {
    this.loaded = true;
  }

  @Mutation
  startLoading() {
    this.loadingCounter += 1;
  }

  @Mutation
  stopLoading() {
    this.loadingCounter -= 1;
  }

  @Mutation
  setToken(token: string) {
    this.token = token;
  }

  @Mutation
  setUTMParam({ key, value }: { key: keyof FormUTMParams, value: string }) {
    this.utmParams[key] = value;
  }

  @Mutation
  setType(type: FormType) {
    this.type = type;
  }

  @Mutation
  setFormState(formState: FormState) {
    this.formState = formState;
  }

  @Mutation
  setLoanData(loan: CustomerLoanData | InvestmentLoanData) {
    this.loan = loan;
  }

  @Mutation
  setPersonData(person: CustomerPersonData | InvestmentPersonData) {
    this.person = person;
  }

  @Mutation
  setConfig(configData: ConfigInterface | null) {
    this.config = configData;
  }

  @Mutation
  setFormConsent(formConsent: boolean) {
    this.formConsent = formConsent;
  }

  @Mutation
  resetForm() {
    this.formState = null;
    this.token = '';
    this.loan = null;
    this.person = null;
    this.config = null;
    this.utmParams = {};
  }

  @Mutation
  setIsOffer(isOffer) {
    this.isOffer = isOffer;
  }

  // Mutations for person data
  @Mutation
  setPersonField({ field, value }: SetPersonField) {
    this.person[field] = value;
  }

  @Mutation
  setRealtyAddress(realtyAddress: Address | null) {
    if (this.person?.realtyAddress) {
      this.person.realtyAddress = realtyAddress;
    }
  }

  @Mutation
  setHaveRealty(haveRealty: boolean) {
    this.person.haveRealty = haveRealty;
  }

  // Mutations for loan data
  @Mutation
  setInstallmentValue(installmentValue: string) {
    this.loan.installmentValue = installmentValue;
  }

  @Mutation
  setValue(value: string) {
    this.loan.value = value;
  }

  @Mutation
  setDeptors(value: (CustomerPersonData | InvestmentPersonData)[]) {
    this.debtors = value;
  }

  @Mutation
  setPurpose(purpose: string) {
    (this.loan as InvestmentLoanData).purpose = purpose;
  }

  @Mutation
  setOfferDraft(offer: Offer | null) {
    this.offerDraft = offer;
  }

  @Mutation
  setIrregularOfferDraft(irregularOffer: IrregularOffer | null) {
    this.irregularOfferDraft = irregularOffer;
  }

  // ACTIONS
  @Action
  startNewForm() {
    this.setLoanData({
      ...this.type === FormType.ConsumerLoan ? DEFAULT_CUSTORMER_LOAN_DATA : DEFAULT_INVESTMENT_LOAN_DATA,
    });

    this.setPersonData({
      ...this.type === FormType.ConsumerLoan ? DEFAULT_CUSTORMER_PERSON_DATA : DEFAULT_INVESTMENT_PERSON_DATA,
    });

    this.setToken('');

    this.setConfig(null);
  }

  @Action
  async getForm() {
    const orderApi = new FormsApi(getApiConfiguration());

    const {
      data: { debtors = [], ...rowData },
      state,
      config: formConfig,
      type,
    } = await orderApi.getForm({ token: this.token, rawData: true }) as unknown as RetrieveFormFixed;

    const data: RawPersonInfo = {
      ...rowData.basic_person_info,
      ...rowData.collateral,
    };

    // TYPE
    if (data.deal_type || type) this.setType(type === FormType.ConsumerLoanLeadform ? type : data.deal_type);
    // STATE
    if (state) this.setFormState(state);
    // CONFIG
    if (formConfig) {
      this.setConfig({
        ...formConfig.first_name && { firstName: formConfig.first_name },
        ...formConfig.last_name && { lastName: formConfig.last_name },
        ...formConfig.email && { email: formConfig.email },
        ...formConfig.phone && { phone: formConfig.phone },
        ...formConfig.birth_number && { birthNumber: formConfig.birth_number },
        ...formConfig.permanent_address && { permanentAddress: formConfig.permanent_address },
        ...formConfig.identity_card && { identityCard: formConfig.identity_card },
        ...formConfig.self_employed_id && { selfEmployedCompany: formConfig.self_employed_id },
        ...formConfig.installment_value && { installmentValue: formConfig.installment_value },
        ...formConfig.value && { value: formConfig.value },
        ...formConfig.purpose && { purpose: formConfig.purpose },
        ...formConfig.realty_address && { realtyAddress: formConfig.realty_address },
        ...formConfig.have_realty && { haveRealty: formConfig.have_realty },
      });
    }
    // PERON AND LOAN INFO
    if (data) {
      this.setLoanData({
        ...data.deal_type === FormType.ConsumerLoan ? DEFAULT_CUSTORMER_LOAN_DATA : DEFAULT_INVESTMENT_LOAN_DATA,
        ...data.installment_value && { installmentValue: data.installment_value },
        ...data.value && { value: data.value },
        ...(data.deal_type === FormType.InvestmentLoan && data.purpose) && { purpose: data.purpose },
      });

      this.setPersonData({
        ...data.deal_type === FormType.ConsumerLoan ? DEFAULT_CUSTORMER_PERSON_DATA : DEFAULT_INVESTMENT_PERSON_DATA,
        ...data.first_name && { firstName: data.first_name },
        ...data.last_name && { lastName: data.last_name },
        ...data.email && { email: data.email },
        ...data.phone && { phone: data.phone },
        ...data.birth_number && { birthNumber: data.birth_number },
        ...data.permanent_address && { permanentAddress: data.permanent_address },
        ...data.identity_card && { identityCard: data.identity_card },
        ...data.realty_address && { realtyAddress: data.realty_address },
        ...data.have_realty && { haveRealty: data.have_realty },
        ...data.self_employed_id && { selfEmployedCompany: data.self_employed_id },
      });
    }

    // DEBTORS
    this.setDeptors(debtors.map((debtor) => ({
      ...debtor.first_name && { firstName: debtor.first_name },
      ...debtor.last_name && { lastName: debtor.last_name },
      ...debtor.email && { email: debtor.email },
      ...debtor.phone && { phone: debtor.phone },
      ...debtor.birth_number && { birthNumber: debtor.birth_number },
      ...debtor.permanent_address && { permanentAddress: debtor.permanent_address },
      ...debtor.identity_card && { identityCard: debtor.identity_card },
      ...debtor.company_id && { selfEmployedCompany: debtor.company_id },
    } as CustomerPersonData | InvestmentPersonData)) || []);
  }

  @Action
  async createForm() {
    const {
      data: {
        basic_person_info: {
          deal_type = '',
        } = {},
      } = {},
      state,
      token,
    } = await getFormsApi().createForm({
      createForm: this.personInfoData,
      ...this.utmParams,
    });

    if (token) this.setToken(token);
    if (state) this.setFormState(state as FormState);
    if (deal_type) this.setType(deal_type as FormType);
  }

  @Action
  async submitPersonInfo() {
    const {
      data: {
        basic_person_info: {
          deal_type = '',
        } = {},
      } = {},
      state,
      token,
    } = await getFormsApi().submitForm({
      token: this.token,
      createForm: this.personInfoData,
      ...this.utmParams,
    });

    if (token) this.setToken(token);
    if (state) this.setFormState(state as FormState);
    if (deal_type) this.setType(deal_type as FormType);
  }

  @Action
  async submitBankingInfo() {
    const {
      data: {
        basic_person_info: {
          deal_type = '',
        } = {},
      } = {},
      state,
      token,
    } = await getFormsApi().submitForm({
      token: this.token,
      createForm: this.bankingInfoData,
      ...this.utmParams,
    });

    if (token) this.setToken(token);
    if (state) this.setFormState(state as FormState);
    if (deal_type) this.setType(deal_type as FormType);
  }

  @Action
  async submitCollateralInfo() {
    const {
      data: {
        basic_person_info: {
          deal_type = '',
        } = {},
      } = {},
      state,
      token,
    } = await getFormsApi().submitForm({
      token: this.token,
      createForm: this.collateralInfoData,
      ...this.utmParams,
    });

    if (token) this.setToken(token);
    if (state) this.setFormState(state as FormState);
    if (deal_type) this.setType(deal_type as FormType);
  }

  @MutationAction({ mutate: ['requisitionAccounts'] })
  async checkRequisitionAccounts({ screening }: {screening: boolean }) {
    const { data: { accounts_connected: accountsConnected, sufficient_transactions: sufficientTransactions } } = await getFormsApi().askDetailForm({
      token: this.token,
      detailQuestion: {
        type: 'REQUISITION_ACCOUNTS',
        data: {
          ...screening && { screening: true },
        },
      },
    });

    return {
      requisitionAccounts: {
        accountsConnected,
        sufficientTransactions,
      },
    };
  }

  @Action
  async applyOffer(data: Offer) {
    await getFormsApi().askDetailForm({
      token: this.token,
      detailQuestion: {
        type: 'SAVE_APPLICATION_OFFER',
        data,
      },
    });
  }

  @Action
  async applyIrregularOffer(data: IrregularOffer) {
    await getFormsApi().askDetailForm({
      token: this.token,
      detailQuestion: {
        type: 'SAVE_IRREGULAR_PAYMENT_OFFER',
        data,
      },
    });
  }

  @Action
  async createDebtor(data: Partial<RawPersonInfo>) {
    await getFormsApi().askDetailForm({
      token: this.token,
      detailQuestion: {
        type: 'CREATE_DEBTORS',
        data,
      },
    });
  }
}
